import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Star } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { applyBranding } from "../../lib/helpers";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Stars } from "../Content";
import PublicFrame from "./PublicFrame";

function useEmbed() {
  const [params] = useSearchParams();
  return params.get("embed") === "1" ? "?embed=1" : "";
}

function Subscribe({ slug }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [done, setDone] = useState(false);
  const go = async () => { try { await api.post(`/public/c/${slug}/subscribe`, { email, first_name: name }); setDone(true); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="rounded-[var(--radius)] border bg-card p-6 space-y-3" data-testid="blog-subscribe">
      <h3 className="font-semibold">Get new articles by email</h3>
      {done ? <p className="text-sm text-emerald-700" data-testid="blog-subscribed">Thank you for subscribing!</p> : (
        <div className="flex flex-col sm:flex-row gap-2">
          <Input placeholder="First name" data-testid="subscribe-name-input" value={name} onChange={(e) => setName(e.target.value)} />
          <Input type="email" placeholder="Email" data-testid="subscribe-email-input" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button className="rounded-full" data-testid="subscribe-btn" disabled={!email} onClick={go}>Subscribe</Button>
        </div>
      )}
    </div>
  );
}

function Body({ text }) {
  return (text || "").split(/\n\s*\n/).map((p, i) => p.startsWith("## ")
    ? <h2 key={i} className="text-xl font-bold mt-8 mb-2">{p.slice(3)}</h2>
    : <p key={i} className="leading-relaxed mb-4 whitespace-pre-wrap">{p}</p>);
}

export function BlogPage() {
  const { slug, post } = useParams();
  const embed = useEmbed();
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get(post ? `/public/c/${slug}/posts/${post}` : `/public/c/${slug}/posts`).then((r) => { setD(r.data); applyBranding(r.data.branding); }).catch(() => setD(false));
  }, [slug, post]);
  if (d === false) return <PublicFrame embed={!!embed}><p data-testid="blog-not-found">Not found.</p></PublicFrame>;
  if (!d) return <PublicFrame embed={!!embed}><p>…</p></PublicFrame>;
  return (
    <PublicFrame branding={d.branding} embed={!!embed}>
      {post ? (
        <article data-testid="blog-article">
          <Link to={`/blog/${slug}${embed}`} className="text-sm text-muted-foreground">← All articles</Link>
          <h1 className="text-3xl sm:text-4xl font-bold mt-4 mb-2">{d.post.title}</h1>
          <p className="text-xs text-muted-foreground mb-6">{d.post.author} · {(d.post.published_at || "").slice(0, 10)}</p>
          {d.post.cover && <img src={d.post.cover} alt="" className="w-full rounded-[var(--radius)] mb-8 aspect-[16/9] object-cover" />}
          <Body text={d.post.body} />
          <div className="mt-10"><Subscribe slug={slug} /></div>
        </article>
      ) : (
        <div className="space-y-6">
          <h1 className="text-3xl sm:text-4xl font-bold">{d.blog_title}</h1>
          {d.blog_intro && <p className="text-muted-foreground">{d.blog_intro}</p>}
          <div className="grid sm:grid-cols-2 gap-4" data-testid="blog-list">
            {!d.posts.length && <p className="text-sm text-muted-foreground">No articles yet.</p>}
            {d.posts.map((p) => (
              <Link key={p.id} to={`/blog/${slug}/${p.slug}${embed}`} data-testid={`blog-card-${p.slug}`} className="lift rounded-[var(--radius)] border bg-card overflow-hidden">
                {p.cover && <img src={p.cover} alt="" className="w-full aspect-[16/9] object-cover" />}
                <div className="p-4"><div className="font-semibold">{p.title}</div><p className="text-sm text-muted-foreground mt-1 line-clamp-3">{p.excerpt}</p></div>
              </Link>
            ))}
          </div>
          <Subscribe slug={slug} />
        </div>
      )}
    </PublicFrame>
  );
}

export function ReviewsPage() {
  const { slug } = useParams();
  const embed = useEmbed();
  const [d, setD] = useState(null);
  const [f, setF] = useState({ name: "", email: "", rating: 5, text: "" });
  const [sent, setSent] = useState(false);
  useEffect(() => { api.get(`/public/c/${slug}/reviews`).then((r) => { setD(r.data); applyBranding(r.data.branding); }).catch(() => setD(false)); }, [slug]);
  const submit = async () => { try { await api.post(`/public/c/${slug}/reviews`, f); setSent(true); } catch (e) { toast.error(errMsg(e)); } };
  if (d === false) return <PublicFrame embed={!!embed}><p>Not found.</p></PublicFrame>;
  if (!d) return <PublicFrame embed={!!embed}><p>…</p></PublicFrame>;
  return (
    <PublicFrame branding={d.branding} embed={!!embed}>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div><h1 className="text-3xl sm:text-4xl font-bold">Reviews</h1>
          {d.average && <div className="flex items-center gap-2 mt-2" data-testid="reviews-average"><Stars n={Math.round(d.average)} size="h-5 w-5" /><b>{d.average}</b><span className="text-sm text-muted-foreground">({d.count})</span></div>}</div>
        {d.google_review_url && <a href={d.google_review_url} target="_blank" rel="noreferrer" data-testid="google-review-btn"><Button variant="outline" className="rounded-full">Review us on Google</Button></a>}
      </div>
      <div className="space-y-3 mb-10" data-testid="public-reviews-list">
        {d.reviews.map((r) => (
          <div key={r.id} className="rounded-[var(--radius)] border bg-card p-5 space-y-2">
            <div className="flex items-center gap-3"><Stars n={r.rating} /><b className="text-sm">{r.name}</b><span className="text-xs text-muted-foreground">{r.created_at.slice(0, 10)}</span></div>
            <p className="text-sm whitespace-pre-wrap">{r.text}</p>
            {r.reply && <p className="text-sm text-muted-foreground border-l-2 pl-3">{r.reply}</p>}
          </div>
        ))}
      </div>
      <div className="rounded-[var(--radius)] border bg-card p-6 space-y-3" data-testid="review-form">
        <h2 className="text-lg font-semibold">Write a review</h2>
        {sent ? <p className="text-sm text-emerald-700" data-testid="review-sent">Thank you! Your review will appear once approved.</p> : (
          <>
            <div className="flex gap-1">{[1, 2, 3, 4, 5].map((i) => <button key={i} aria-label={`${i} stars`} data-testid={`review-star-${i}`} onClick={() => setF({ ...f, rating: i })}><Star className={`h-7 w-7 transition-colors ${i <= f.rating ? "fill-[hsl(var(--brand-accent))] text-[hsl(var(--brand-accent))]" : "text-muted-foreground/40"}`} /></button>)}</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <Input placeholder="Your name" data-testid="review-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} />
              <Input type="email" placeholder="Email (not published)" data-testid="review-email-input" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            </div>
            <Textarea rows={4} placeholder="Your experience" data-testid="review-text-input" value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} />
            <Button className="rounded-full" data-testid="review-submit-btn" disabled={!f.name || !f.email || f.text.length < 3} onClick={submit}>Submit review</Button>
          </>
        )}
      </div>
    </PublicFrame>
  );
}

export function Unsubscribe() {
  const { token } = useParams();
  const [state, setState] = useState("…");
  useEffect(() => { api.post(`/public/unsubscribe/${token}`).then(() => setState("You have been unsubscribed.")).catch(() => setState("This link is not valid.")); }, [token]);
  return <PublicFrame><p data-testid="unsubscribe-result">{state}</p></PublicFrame>;
}
